import type { BaseDocument } from '../mongo/base-document.interface';
import type {
  AutomationMode,
  BaseCurrencyFxPolicy,
  BillingSource,
  DocumentCategory,
  ExportCadence,
  ExportDelivery,
  ExportFormat,
  GroupingKey,
  IntegrationMode,
  PaymentStatusSource,
} from './enums';
import { TERRITORY_CURRENCY } from '../territory/territory.interface';
import type { TerritoryCurrency } from '../territory/territory.interface';
import type { CurrencyCode, DecimalString, Money } from './money';

/**
 * SWOOP-430: the language an invoicing document (invoice, credit note) is rendered in. A superset of
 * `TerritoryLanguage` (`en`/`fr`) — a tenant may invoice in Arabic even where the territory UI is English or
 * French — so it is its own const, not `TERRITORY_LANGUAGE`.
 */
export const DOCUMENT_LANGUAGE = {
  AR: 'ar',
  EN: 'en',
  FR: 'fr',
} as const;

export type DocumentLanguage = (typeof DOCUMENT_LANGUAGE)[keyof typeof DOCUMENT_LANGUAGE];

/**
 * SWOOP-430: one dated conversion rate from the territory's base currency to a reporting `currency`
 * ("`<baseCurrency>` 1 = `rate` `<currency>`"). Each rate belongs to a `currency` (a territory may report in
 * several over time). Rates are ADD-ONLY: a wrong one is corrected by a later-dated entry, never edited or
 * removed. At most one rate per (`currency`, day); `currency` must not equal the row's `baseCurrency`. `rate` is
 * a positive decimal; `source` is where it came from.
 */
export interface BillingExchangeRate {
  currency: TerritoryCurrency;
  effectiveFrom: Date;
  rate: DecimalString;
  source: string;
}

/**
 * SWOOP-430: the limits of a {@link BillingExchangeRate}, declared ONCE here so the two backends (DTO edge and
 * service) and the front-office form all import the same numbers (code-conventions §DRY). Changing a limit here
 * changes it everywhere.
 */
/** The longest an exchange-rate `source` label may be (a free-text field, bounded so a stored rate stays sane). */
export const BILLING_EXCHANGE_RATE_SOURCE_MAX_LENGTH = 200;
/**
 * The most rates one save may ADD. Rates are add-only and appended server-side, so this bounds a single save, never
 * the stored total — a territory can keep adding rates indefinitely across saves.
 */
export const BILLING_EXCHANGE_RATES_ADDED_PER_SAVE_MAX = 50;
/** The currencies a rate may be for (every territory currency); the base-currency exclusion is enforced per save. */
export const BILLING_EXCHANGE_RATE_CURRENCIES: readonly TerritoryCurrency[] = Object.values(TERRITORY_CURRENCY);

/** Design §3.1. `byCategory` overrides `mode` for one document category. */
export interface AutomationPolicy {
  autoIssueMaxAmount?: Money;
  autoIssueSenderName?: string;
  byCategory?: Partial<Record<DocumentCategory, AutomationMode>>;
  /** Percent of failed drafts above which the run issues nothing (default 20). */
  circuitBreakerFailurePercent?: number;
  /** Drafts evaluated before the breaker can trip (default 10). */
  circuitBreakerMinDrafts?: number;
  /** Percent a contract's lines may change against its previous invoice (default 20). */
  maxChangeVsPreviousPercent?: number;
  mode: AutomationMode;
  /** The kill switch. */
  paused: boolean;
  pausedAt?: Date | null;
  pausedBy?: string | null;
  /** Incremented on every change; recorded on the audit event and on a `SYSTEM` approval. */
  version: number;
}

export interface SourceSyncSettings {
  enabled: boolean;
  /** Tenant-local time of the daily job. */
  localTime: string;
  providers: BillingSource[];
  /** Last committed sync position per provider; advances only after the batch commits. */
  watermark: Partial<Record<BillingSource, Date>>;
}

export interface ExportScheduleSettings {
  attachmentRecipientAllowList: string[];
  cadence: ExportCadence;
  dayOfMonth?: number;
  dayOfWeek?: number;
  delivery: ExportDelivery;
  enabled: boolean;
  format: ExportFormat;
  localTime: string;
  notifyWhenEmpty: boolean;
  recipients: string[];
}

export interface ApprovalPolicy {
  distinctApprover: boolean;
  required: boolean;
  thresholdAmount?: DecimalString;
}

/** The monthly schedule. Only the fields the dispatcher index reads are typed so far. */
export interface MonthlyRunSettings {
  enabled: boolean;
  /** UTC instant of the next run. */
  nextRunAt: Date | null;
}

export interface GroupingPolicy {
  /** Per document category, the ordered optional grouping keys. */
  categories: Partial<Record<DocumentCategory, GroupingKey[]>>;
  version: string;
}

export interface RechargePolicyEntry {
  serviceFeePercent: DecimalString;
  vatOnRecharge: boolean;
  vatOnServiceFee: boolean;
}

export interface PaymentInstructions {
  accountHolder?: string;
  accountNumber?: string;
  bankName?: string;
  iban?: string;
  note?: string;
  swift?: string;
}

export interface ReminderPolicy {
  ccEmails: string[];
  enabled: boolean;
  /** Days after `dueDate + gracePeriodDays`, for example `[3, 14, 30]`. */
  offsetsDays: number[];
  templateRef?: string | null;
}

/**
 * One billing policy record per tenant and territory (design §3.1). A row with `territoryId: null` is the
 * tenant default that a territory row overrides. `invoiceAuthority` and `accountingAuthority` are
 * post-MVP and not stored.
 */
export interface TenantBillingSettings extends BaseDocument {
  approvalPolicy: ApprovalPolicy;
  automationPolicy: AutomationPolicy;
  baseCurrency: CurrencyCode;
  baseCurrencyFxPolicy: BaseCurrencyFxPolicy;
  /** Set when the territory moves from `FILE_EXPORT` to an ERP mode; null while it exports files. */
  cutoverAt: Date | null;
  defaultInvoiceCurrency: CurrencyCode;
  defaultIssuingLocationId: string | null;
  /** SWOOP-430: the language invoicing documents are rendered in. Defaults to the territory language on a first save. */
  documentLanguage: DocumentLanguage;
  documentTemplateRef: string | null;
  enabledInvoiceCurrencies: CurrencyCode[];
  /** SWOOP-430: dated base→reporting conversion rates, add-only, ascending by `effectiveFrom`. Empty when none. */
  exchangeRates: BillingExchangeRate[];
  exportSchedule: ExportScheduleSettings | null;
  fxPolicyRef: string | null;
  groupingPolicy: GroupingPolicy;
  integrationMode: IntegrationMode;
  monthlyRun: MonthlyRunSettings | null;
  paymentInstructions: PaymentInstructions | null;
  paymentStatusSource: PaymentStatusSource;
  rechargePolicy: Partial<Record<'DAMAGE_RECHARGE' | 'FINE_RECHARGE', RechargePolicyEntry>>;
  reminderPolicy: ReminderPolicy;
  /** SWOOP-430: the currency the tenant reports billing figures in; null until a first billing-defaults save sets it. */
  reportingCurrency: CurrencyCode | null;
  requireAccountingCodesAtIssue: boolean;
  sourceSync: SourceSyncSettings;
  taxPolicyRef: string | null;
  territoryId: string | null;
}

export type CreateTenantBillingSettings = Omit<TenantBillingSettings, keyof BaseDocument>;

/** SWOOP-430: one exchange rate on the wire (dates are ISO 8601 strings, never `Date`). Each rate carries its own reporting `currency`. */
export interface BillingExchangeRateView {
  currency: TerritoryCurrency;
  effectiveFrom: string;
  rate: DecimalString;
  source: string;
}

/**
 * SWOOP-430: the Territory › Billing defaults the front office reads and writes (admin-auth
 * `GET`/`PUT /billing-settings`). It is the billing-defaults SUBSET of `TenantBillingSettings` — never the
 * whole invoicing policy. `id` and `updatedAt` are null while the territory has never saved (the first
 * state); `baseCurrency` is the territory's CURRENT currency, read live and not stored on this view;
 * `reportingCurrency` is null in the first state. `updatedAt` is echoed back as the save's `baseUpdatedAt`.
 */
export interface BillingSettingsView {
  baseCurrency: TerritoryCurrency;
  documentLanguage: DocumentLanguage;
  exchangeRates: BillingExchangeRateView[];
  id: string | null;
  reportingCurrency: TerritoryCurrency | null;
  territoryId: string;
  updatedAt: string | null;
}
